import { Component, ChangeDetectionStrategy, ElementRef, inject, signal, computed, viewChild, NgZone } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatIconModule } from '@angular/material/icon';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { MetaService } from '@zerops/zef/meta';
import { selectZefNgrxRouterIdByKey, selectZefNgrxRouterUrl } from '@zerops/zef/ngrx-router';
import { RouteKeys } from '@zerops/zerops/app';
import { ServiceStackEntity } from '@zerops/zerops/core/service-stack-base';
import { UserDataEntity } from '@zerops/zerops/core/user-data-base';
import { PROJECT_BASE_FEATURE_NAME, ProjectEntity } from '@zerops/zerops/core/project-base';
import { combineLatest, EMPTY, fromEvent, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, pairwise, startWith, switchMap, take, tap, timeout, withLatestFrom } from 'rxjs/operators';
import { detectPublicAccess, extractServiceMetadata } from '@zerops/zerops/core/zerops-services';
import { zcpAgentAuthDialogActions, ZCP_AGENT_AUTH_DIALOG_FEATURE_NAME } from '@zerops/zerops/feature/zcp-agent-auth-dialog';
import { ZagentAgentType } from '@zerops/zerops/core/zerops-services/zerops-services.model';
import { OverlayTrayService } from '../overlay-tray';
import { FEATURE_NAME } from './code-server-overlay.constant';
import { CodeServerOverlayMeta } from './code-server-overlay.model';
import { CodeServerOverlayService } from './code-server-overlay.service';
import { codeServerOverlayOpen } from './code-server-overlay.state';
import {
  AGENT_AUTH_BRIDGE_CHANNEL,
  AGENT_AUTH_BRIDGE_ACK_TYPE,
  AgentAuthBridgeAckMessage,
  AgentAuthBridgeAckReason,
  AgentAuthBridgeContext,
  EmbedWizardMode,
  ackOutcomeForManualOpenResult,
  buildSetModeMessage,
  validateAgentAuthBridgeMessage
} from './code-server-overlay.bridge';
import { ZcpOnboardWizardService } from '@zerops/zerops/core/zcp-pool-claim-base';

// Cap on the per-open dedup set (replay guard). Structural/framing constants,
// the bridge message/context types, and the pure verdict live in
// ./code-server-overlay.bridge; the component owns only the side effects.
const MAX_SEEN_BRIDGE_EVENTS = 100;

// Bound on waiting for `bridgeContext` before dispatching the wizard's own
// auth-dialog open (spec-welcome-mode.md §8.1): without this, a container
// that never resolves a usable URL leaves the wizard waiting forever in
// `authorizing` — the auth dialog's OWN 10s "unreachable" timer
// (zcp-agent-auth-dialog.effect.ts) never even gets a chance to start.
const WIZARD_AUTH_BRIDGE_CONTEXT_TIMEOUT_MS = 15_000;

@Component({
  selector: 'z-code-server-overlay',
  templateUrl: './code-server-overlay.feature.html',
  styleUrl: './code-server-overlay.feature.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class CodeServerOverlayFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #actions = inject(Actions);
  #sanitizer = inject(DomSanitizer);
  #serviceStackEntity = inject(ServiceStackEntity);
  #projectEntity = inject(ProjectEntity);
  #userDataEntity = inject(UserDataEntity);
  #tray = inject(OverlayTrayService);
  #onboardWizard = inject(ZcpOnboardWizardService);
  #ngZone = inject(NgZone);
  #meta = inject(MetaService);
  #seenBridgeEventIds = new Set<string>();
  // Deferred bridge acks: an accepted request's Window/origin, keyed by the
  // inbound eventId (== the dispatched `manualOpen` requestId). Window refs stay
  // in the component — never inside an NgRx action. Resolved when the dialog-open
  // effect reports back via `manualOpenResult`; cleared on overlay close/reset.
  #pendingBridgeAcks = new Map<string, { source: MessageEventSource | null; origin: string }>();
  // Correlates the wizard's OWN `manualOpen` dispatch (fired when the wizard
  // enters `authorizing`, §8.1) with the matching `manualOpenResult`, so the
  // outcome can be routed onto the wizard without colliding with
  // `#pendingBridgeAcks`'s bridge-triggered requestIds (both listen to the
  // same action type; requestIds never collide since each is minted fresh
  // per flow).
  #pendingWizardAuthRequestId: string | undefined;
  // True once OUR `manualOpen` request resolved with `ok:true` — meaning the
  // dialog OPENED, nothing more (manualOpenResult.ok never meant "auth
  // succeeded", only "the dialog is open"). Makes a subsequent open->close
  // transition on the auth dialog meaningful to the wizard-close watcher
  // below; before this flips, a close event isn't ours to react to.
  #wizardAuthDialogOpened = false;
  overlay = inject(CodeServerOverlayService);

  // # View Children
  iframeRef = viewChild<ElementRef<HTMLIFrameElement>>('codeServerIframe');

  // # Event Streams
  onClose$ = new Subject<void>();
  onMinimize$ = new Subject<void>();
  onMaximize$ = new Subject<void>();
  onUnmaximize$ = new Subject<void>();

  // # Data — sync
  key = FEATURE_NAME;
  sanitizedUrl = signal<SafeResourceUrl | null>(null);
  bridgeContext = signal<AgentAuthBridgeContext | null>(null);
  // `toObservable()` needs an injection context, so it must be created once
  // here (a field initializer) — NOT re-invoked inside a later `switchMap`
  // callback, which runs outside any injection context and would throw.
  #bridgeContext$ = toObservable(this.bridgeContext);
  projectName = signal('');
  serviceName = signal('');

  // Detached geometry expressed as viewport insets rather than width/height.
  // When the editor detaches from the control-plane page we keep its exact
  // docked rect (top/left) but render it via right/bottom insets so a
  // position:fixed box holds those margins constant and auto-resizes with the
  // viewport — no shift on navigation, yet responsive on project detail.
  // Recomputes only when `position()` changes (i.e. while docked); off-page the
  // rect is frozen and CSS handles resize. Null until a docked rect exists.
  detachedInset = computed(() => {
    const pos = this.overlay.position();
    if (!pos) { return null; }
    return {
      right: window.innerWidth - pos.left - pos.width,
      bottom: window.innerHeight - pos.top - pos.height
    };
  });

  // # Data — async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  meta$ = this.dialogState$.pipe(
    map((data) => data?.meta as CodeServerOverlayMeta | undefined)
  );
  // Re-subscribe on each open→close→open cycle so distinctUntilChanged resets,
  // allowing the same serviceStackId to re-trigger URL resolution after close+reopen
  serviceStackId$ = this.open$.pipe(
    distinctUntilChanged(),
    switchMap((open) => open
      ? this.meta$.pipe(
          map((m) => m?.serviceStackId),
          filter((id): id is string => !!id),
          distinctUntilChanged()
        )
      : EMPTY
    )
  );

  // Current route's service stack ID and URL
  routeServiceStackId$ = this.#store.pipe(
    select(selectZefNgrxRouterIdByKey(RouteKeys.ServiceStackDetail))
  );
  routeUrl$ = this.#store.pipe(select(selectZefNgrxRouterUrl));

  // Whether we're on the same service's detail subpage (any subpage)
  isOnSameServicePage$ = combineLatest([
    this.serviceStackId$,
    this.routeServiceStackId$
  ]).pipe(
    map(([ overlayId, routeId ]) => routeId === overlayId),
    distinctUntilChanged()
  );

  // Whether the current route is the control-plane page for the overlay's service
  isOnControlPlanePage$ = combineLatest([
    this.isOnSameServicePage$,
    this.routeUrl$
  ]).pipe(
    map(([ sameService, url ]) =>
      sameService && !!url?.endsWith('/control-plane')
    ),
    distinctUntilChanged()
  );

  // Hide bubble only when on the control-plane page of the same service
  hideBubble$ = this.isOnControlPlanePage$;

  // Open but away from the control-plane page: there's no `#iframePlaceholder`
  // to dock into (that page owns `overlay.position()` and its resize handler),
  // so the last docked rect is stale and can't track resize. Present full-screen
  // instead and let CSS handle sizing.
  isDetached$ = combineLatest([
    this.open$,
    this.isOnControlPlanePage$
  ]).pipe(
    map(([ open, onControlPlane ]) => !!open && !onControlPlane),
    distinctUntilChanged()
  );

  // # State resolver
  state = this.$connect({
    open: this.open$,
    isDetached: this.isDetached$
  });

  // # Dispatcher
  constructor() {
    super();

    // Agent-auth bridge: the embedded code-server webview can ask this app
    // to open the existing agent-auth dialog, without ever seeing
    // credentials — the message is trigger-only (agentType + a fresh
    // eventId), nothing else crosses the boundary. serviceStackId and
    // clientId always come from Angular state (bridgeContext above), never
    // from the message. The listener only runs while the overlay is open
    // (see open$) — no app-lifetime listener while it's closed. A message
    // must pass every structural/origin/iframe-identity/freshness check,
    // be unseen, AND name a supported agent to get accepted; anything that
    // fails a structural check stays completely silent (no ack) so a
    // probing frame has no oracle to learn why it failed. A structurally
    // valid request for an unsupported agent still gets a rejecting ack
    // so the sender doesn't hang on its own timeout for no reason.
    //
    // Kept OUT of $dispatchActions and run outside the Angular zone: EVERY
    // window 'message' on the page (devtools, unrelated iframes) would
    // otherwise trigger app-wide change detection while the overlay is open.
    // Only the store dispatch on an accepted request re-enters the zone —
    // validation, dedup, the ack postMessage, and the debug logs all stay
    // outside it.
    const bridgeMessages$ = this.open$.pipe(
      distinctUntilChanged(),
      tap((open) => console.debug(`[code-server bridge] message listener ${open ? 'attach' : 'detach'}`)),
      switchMap((open) => open ? this.#bridgeWindowMessages$() : EMPTY),
      mergeMap((event) => this.#handleBridgeMessage(event)),
      takeUntilDestroyed()
    );
    this.#ngZone.runOutsideAngular(() => bridgeMessages$.subscribe());

    // Browser tab identity: while ZCP is on screen (docked or fullscreen — NOT
    // while minimized to the tray) the tab reads `<project> ~ ZCP ~ zerops.io`,
    // so two ZCP tabs are tellable apart. `MetaService.lockTitle` holds the tab
    // against the subpage `meta.set()` calls that fire as the user clicks
    // around underneath the overlay; on minimize/close we release it and the
    // page below gets its own title back. `projectName` resolves a beat after
    // open, so this re-locks (swapping the name in, keeping the restore
    // snapshot) once it lands. Full name, no truncation — by design it will
    // overflow the tab strip and that's fine.
    //
    // Kept OUT of $dispatchActions (it dispatches nothing) and on its own
    // subscription so a fault in the title path can never tear down the
    // action-producing effects below — same rationale as `bridgeMessages$`.
    combineLatest([
      this.open$,
      toObservable(this.overlay.minimized),
      toObservable(this.projectName)
    ]).pipe(
      map(([ open, minimized, projectName ]) => open && !minimized
        ? (projectName ? `${projectName} ~ ZCP` : 'ZCP')
        : null
      ),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe((title) => title === null
      ? this.#meta.unlockTitle()
      : this.#meta.lockTitle(title)
    );

    this.$dispatchActions([
      // ZCP-pool claim pre-warm: the drain stores the found ZCP's id on the
      // onboard wizard service; open ourselves immediately (behind the cover) so
      // the iframe starts resolving/loading before the control-plane page
      // mounts. Re-opening from the page later is a no-op (same id).
      toObservable(this.#onboardWizard.prewarmServiceStackId).pipe(
        filter((id): id is string => !!id),
        distinctUntilChanged(),
        map((serviceStackId) => codeServerOverlayOpen({ serviceStackId }))
      ),

      // Close
      this.onClose$.pipe(
        tap(() => this.#reset()),
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      // Minimize / Maximize / Unmaximize — local state only
      this.onMinimize$.pipe(
        tap(() => this.overlay.minimize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      this.onMaximize$.pipe(
        tap(() => this.overlay.maximize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      this.onUnmaximize$.pipe(
        tap(() => this.overlay.unmaximize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      // When dialog opens with a serviceStackId, resolve URL + names once.
      //
      // Readiness is authoritative: `sanitizedUrl` (the clickable iframe) is set
      // ONLY together with a complete `bridgeContext` (origin + serviceStackId +
      // clientId), or both are cleared — a clickable iframe must never coexist
      // with `bridgeContext === null` (a bridge message would then silently
      // ignore for `context-missing`). clientId is resolved from the PROJECT
      // entity by `stack.projectId`, not the embedded `stack.project` (which this
      // codebase treats as unreliably populated — same path the auth dialog and
      // service-card base use). We wait for stack + project.clientId + url
      // together and freeze nothing partial.
      this.serviceStackId$.pipe(
        // A new serviceStackId means the overlay was reopened for a different
        // service — tear down the previous context BEFORE resolving the new one
        // so there's no window where a stale origin/identity pairs with a fresh
        // message.
        tap(() => {
          this.sanitizedUrl.set(null);
          this.bridgeContext.set(null);
          this.#seenBridgeEventIds.clear();
        }),
        switchMap((id) => this.#serviceStackEntity.entityById$(id).pipe(
          filter((d) => !!d),
          take(1)
        )),
        tap((stack) => {
          this.serviceName.set(stack.name || '');
        }),
        switchMap((stack) => this.#projectEntity.entityById$(stack.projectId).pipe(
          filter((project): project is NonNullable<typeof project> => !!project?.clientId),
          take(1),
          map((project) => ({ stack, project }))
        )),
        tap(({ project }) => {
          this.projectName.set(project.name || '');
        }),
        switchMap(({ stack, project }) => this.#userDataEntity.list$({
          name: `${PROJECT_BASE_FEATURE_NAME}-zagent`,
          id: stack.projectId
        }).pipe(
          filter((d) => !!d),
          take(1),
          map((userData) => ({ stack, project, userData: userData.filter((itm) => itm.serviceStackId === stack.id) }))
        )),
        switchMap(({ stack, project, userData }) => {
          const metadata = extractServiceMetadata(userData);
          if (!metadata?.hasVscode) return of({ stack, project, url: null });

          const access = detectPublicAccess(stack, userData);
          if (access.mode === 'public' && access.url) {
            if (access.authEnabled && access.password) {
              // Cookie-based auth: /zcp-auth/<password> sets __zcp_auth cookie and redirects to /
              return of({ stack, project, url: `${access.url}/zcp-auth/${access.password}` });
            }
            return of({ stack, project, url: `${access.url}?folder=/var/www` });
          }
          return of({ stack, project, url: `http://${stack.name}:8080?folder=/var/www` });
        }),
        tap(({ stack, project, url }) => {
          // Both-or-neither: a resolvable url pairs with the full context; no url
          // (no vscode) leaves both null — either way the invariant holds.
          if (url) {
            this.sanitizedUrl.set(this.#sanitizer.bypassSecurityTrustResourceUrl(url));
            this.bridgeContext.set({
              origin: new URL(url).origin,
              serviceStackId: stack.id,
              clientId: project.clientId
            });
            // Origin only — never the url (it embeds the code-server password).
            console.debug(`[code-server bridge] context ready origin=${new URL(url).origin} stack=${stack.id}`);
          } else {
            this.sanitizedUrl.set(null);
            this.bridgeContext.set(null);
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Deferred bridge ack: the dialog-open effect reports back whether the
      // container resolved (ok) or timed out/was absent (!ok). Look the pending
      // sender up by requestId and ack truthfully — `accepted:true` only when
      // the dialog actually opened, `accepted:false, reason:'not-ready'`
      // otherwise. A request whose pending entry was cleared by a reset in the
      // meantime is simply not acked (the sender's own timeout covers it).
      this.#actions.pipe(
        ofType(zcpAgentAuthDialogActions.manualOpenResult),
        tap(({ requestId, ok }) => {
          const pending = this.#pendingBridgeAcks.get(requestId);
          if (!pending) return;
          this.#pendingBridgeAcks.delete(requestId);
          console.debug(`[code-server bridge] ack event=${requestId.slice(0, 8)} accepted=${ok}`);
          this.#ackBridgeMessage(
            pending.source, pending.origin, requestId,
            ackOutcomeForManualOpenResult(ok)
          );
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Wizard auth (spec-welcome-mode.md §8.1): when the wizard picks an
      // agent and enters `authorizing`, dispatch the existing auth dialog
      // over the layer via the SAME `manualOpen`/`manualOpenResult` path the
      // bridge's own §4.2 flow already uses — "zero rebuild". This
      // component (not the wizard service) holds `serviceStackId`/`clientId`
      // (resolved into `bridgeContext` above), so it mints the request here.
      // Re-read wizard state/pick fresh once bridgeContext resolves — the
      // wizard could have moved on while waiting.
      //
      // Bounded wait: `WIZARD_AUTH_BRIDGE_CONTEXT_TIMEOUT_MS` — a container
      // that never resolves a usable URL must not leave the wizard stuck in
      // `authorizing` forever; on timeout it's routed to
      // `authDialogUnavailable()` instead of dispatching.
      //
      // Busy-dialog gate: if the auth dialog is already open (some other
      // flow — the service-card button, a deploy auto-open — owns it),
      // re-dispatching `manualOpen` would NOT reseed its internal walker
      // (its init is gated on a closed->open transition), so the wizard must
      // never pretend it owns an already-open dialog — it bounces back to
      // `picking` via `authDialogDismissed()` instead.
      toObservable(this.#onboardWizard.state).pipe(
        filter((s) => s === 'authorizing'),
        switchMap(() => this.#bridgeContext$.pipe(
          filter((ctx): ctx is AgentAuthBridgeContext => !!ctx),
          take(1),
          timeout({ each: WIZARD_AUTH_BRIDGE_CONTEXT_TIMEOUT_MS }),
          catchError(() => {
            if (this.#onboardWizard.state() === 'authorizing') {
              this.#onboardWizard.authDialogUnavailable();
            }
            this.#pendingWizardAuthRequestId = undefined;
            this.#wizardAuthDialogOpened = false;
            return EMPTY;
          })
        )),
        withLatestFrom(this.#store.pipe(select(selectZefDialogState(ZCP_AGENT_AUTH_DIALOG_FEATURE_NAME)))),
        map(([ ctx, dialogState ]) => {
          if (this.#onboardWizard.state() !== 'authorizing') return null;

          if (dialogState?.state) {
            this.#onboardWizard.authDialogDismissed();
            return null;
          }

          const agentId = this.#onboardWizard.pickedAgentId();
          if (!agentId) return null;
          const requestId = crypto.randomUUID();
          this.#pendingWizardAuthRequestId = requestId;
          return zcpAgentAuthDialogActions.manualOpen({
            serviceStackId: ctx.serviceStackId,
            agentType: agentId as ZagentAgentType,
            clientId: ctx.clientId,
            requestId,
            // The wizard owns its own post-auth navigation (§8.1's
            // authSucceeded -> launching flow) — the auth
            // dialog's default control-plane redirect would otherwise
            // silently re-dock the wizard's prewarmed fullscreen embed.
            successNavigation: 'none'
          });
        }),
        filter((action): action is ReturnType<typeof zcpAgentAuthDialogActions.manualOpen> => action !== null)
      ),

      // Wizard auth dialog-open bookkeeping: `manualOpenResult` for OUR
      // request (never the bridge's — that's `#pendingBridgeAcks` above,
      // disjoint by requestId). `ok:true` means only that the dialog OPENED
      // — the real auth-success signal is the `markAuthorized` listener
      // below, never this. `ok:false` is a genuine failure to open at all.
      this.#actions.pipe(
        ofType(zcpAgentAuthDialogActions.manualOpenResult),
        filter(({ requestId }) => requestId === this.#pendingWizardAuthRequestId),
        tap(({ ok }) => {
          if (!ok) {
            this.#pendingWizardAuthRequestId = undefined;
            this.#wizardAuthDialogOpened = false;
            this.#onboardWizard.authDialogUnavailable();
            return;
          }
          this.#wizardAuthDialogOpened = true;
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // The REAL auth-success signal (spec-welcome-mode.md §8.1): the
      // `markAuthorized` action the auth dialog dispatches on actual OAuth
      // completion — never `manualOpenResult.ok`, which only ever meant "the
      // dialog opened". Accepted only for OUR picked agent on OUR resolved
      // stack, while still `authorizing` (a stale/unrelated markAuthorized —
      // e.g. from the service-card's own manual flow — must not move this
      // wizard).
      this.#actions.pipe(
        ofType(zcpAgentAuthDialogActions.markAuthorized),
        filter(({ serviceStackId, agentType }) =>
          this.#onboardWizard.state() === 'authorizing'
          && serviceStackId === this.bridgeContext()?.serviceStackId
          && agentType === this.#onboardWizard.pickedAgentId()
        ),
        tap(() => {
          this.#pendingWizardAuthRequestId = undefined;
          this.#wizardAuthDialogOpened = false;
          this.#onboardWizard.authSucceeded();
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Dialog-close watcher: an open->closed transition on the auth
      // dialog's OWN state, while the wizard is still `authorizing` and OUR
      // open request actually resolved (`#wizardAuthDialogOpened`) — the
      // user dismissed it without completing auth. Bounces the wizard back
      // to `picking` (keeping the pick) via `authDialogDismissed()`.
      //
      // Ordering note: on a real OAuth success, `markAuthorized` fires ~1s
      // BEFORE the dialog auto-closes (its own AGENT_AUTH_PAUSE_MS) — by the
      // time this close arrives the wizard is already `launching`, so the
      // state check above makes this a no-op. That's the designed latch, not
      // a race: no separate success flag needed.
      this.#store.pipe(
        select(selectZefDialogState(ZCP_AGENT_AUTH_DIALOG_FEATURE_NAME)),
        map((s) => !!s?.state),
        distinctUntilChanged(),
        pairwise(),
        filter(([ prev, curr ]) => prev === true && curr === false),
        tap(() => {
          if (this.#onboardWizard.state() !== 'authorizing' || !this.#wizardAuthDialogOpened) return;
          this.#onboardWizard.authDialogDismissed();
          this.#pendingWizardAuthRequestId = undefined;
          this.#wizardAuthDialogOpened = false;
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Auto-minimize when navigating from control-plane to another subpage of the same service
      // Uses URL to detect same-service navigation (avoids race with ngrx router ID reset).
      // Gated on `open$`: a CLOSED overlay must never be minimized here — otherwise
      // `minimized` sticks `true` past the close (nothing else clears it once
      // `#reset()` has run) and poisons the next open with a phantom "Restore editor".
      this.isOnControlPlanePage$.pipe(
        startWith(false),
        pairwise(),
        filter(([ prev, curr ]) => prev === true && curr === false),
        withLatestFrom(this.routeUrl$, this.open$),
        filter(([ _pair, url, open ]) => !!open && !!url?.includes('/service-stack/')),
        filter(() => !this.overlay.minimized()),
        tap(() => this.overlay.minimize()),
        map(() => null),
        filter((v) => v !== null)
      ),

      // A fresh open always starts docked/normal. `#reset()` clears minimize/maximize
      // on close, but other flows can still leave them set while the overlay is
      // closed — clear them on the closed→open transition so a stale flag can't
      // hide the next open.
      this.open$.pipe(
        distinctUntilChanged(),
        filter((open) => !!open),
        tap(() => {
          this.overlay.restore();
          this.overlay.unmaximize();
        }),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Reset when dialog closes externally
      this.open$.pipe(
        filter((open) => !open),
        tap(() => this.#reset()),
        map(() => null),
        filter((v) => v !== null)
      ),

      // Tray bubble registration follows open + minimized + hideBubble — a
      // closed overlay must never leave a bubble behind.
      combineLatest([
        this.open$.pipe(startWith(undefined)),
        toObservable(this.overlay.minimized),
        this.hideBubble$.pipe(startWith(false))
      ]).pipe(
        tap(([ open, minimized, hideBubble ]) => {
          if (open && minimized && !hideBubble) {
            this.#tray.register({
              id: FEATURE_NAME,
              caption: this.projectName(),
              title: this.serviceName(),
              onRestore: () => this.overlay.restore(),
              onClose: () => this.onClose$.next()
            });
          } else {
            this.#tray.unregister(FEATURE_NAME);
          }
        }),
        map(() => null),
        filter((v) => v !== null)
      )
    ]);
  }

  #reset() {
    this.overlay.reset();
    this.sanitizedUrl.set(null);
    this.bridgeContext.set(null);
    this.#seenBridgeEventIds.clear();
    // Drop any deferred acks — the overlay (and its iframe) is gone, so there's
    // no sender left to answer and no live context to answer for.
    this.#pendingBridgeAcks.clear();
    this.#pendingWizardAuthRequestId = undefined;
    this.#wizardAuthDialogOpened = false;
    this.projectName.set('');
    this.serviceName.set('');
    // The embed this overlay was showing is gone (closed, or about to be
    // reopened for a different service) — the wizard must not go on
    // addressing that stale window (spec-welcome-mode.md §8.1).
    this.#onboardWizard.onEmbedGone();
  }

  // Wraps `fromEvent` so `addEventListener` itself is called while the
  // Angular zone is inactive — not just at the point this whole pipe was
  // (re)subscribed. `switchMap` (re)subscribes here on every dialog open,
  // and an open is driven by a store dispatch, which normally runs INSIDE
  // the zone; re-entering `runOutsideAngular` on every (re)subscription —
  // not only the outer one in the constructor — is what actually keeps the
  // listener registration out of it.
  #bridgeWindowMessages$(): Observable<MessageEvent> {
    return new Observable<MessageEvent>((subscriber) => {
      let innerSub: Subscription | undefined;
      this.#ngZone.runOutsideAngular(() => {
        innerSub = fromEvent<MessageEvent>(window, 'message').subscribe(subscriber);
      });
      return () => innerSub?.unsubscribe();
    });
  }

  // Validates one inbound bridge postMessage and runs its side effects (dedup
  // recording, ack postMessage, debug logs) — all outside the Angular zone,
  // same as the caller. The ONLY part that re-enters the zone is the store
  // dispatch on an accepted request, since that's what change detection
  // needs to actually see (opening the auth dialog).
  #handleBridgeMessage(event: MessageEvent): Observable<never> {
    // The verdict is a pure function of plain data (see the bridge module) —
    // identity lookup, freshness, dedup, and the supported-agent gate all
    // decided in one atomic snapshot. serviceStackId and clientId on an
    // `accept` come from that snapshot's context, never from the message.
    const verdict = validateAgentAuthBridgeMessage({
      data: event.data,
      origin: event.origin,
      context: this.bridgeContext(),
      fromCurrentIframe: () => this.#isFromCurrentIframe(event.source),
      seenEventIds: this.#seenBridgeEventIds,
      now: Date.now()
    });

    // Silent drop — no ack, no dispatch: a probing frame learns nothing.
    // One local diagnostic line (never crosses the wire) records why.
    if (verdict.verdict === 'ignore') {
      console.debug(
        `[code-server bridge] ignored reason=${verdict.reason}`
        + ` event=${(verdict.eventId ?? '').slice(0, 8)}`
        + ` ageMs=${verdict.ageMs ?? 'n/a'}`
        + ` contextReady=${this.bridgeContext() !== null}`
      );
      return EMPTY;
    }

    // Every non-ignore verdict consumes the eventId, so a replay of any of
    // them is silently dropped on redelivery.
    this.#recordSeenEvent(verdict.eventId);

    if (verdict.verdict === 'unsupported') {
      this.#ackBridgeMessage(
        event.source, event.origin, verdict.eventId,
        { accepted: false, reason: 'unsupported-agent' }
      );
      return EMPTY;
    }

    if (verdict.verdict === 'embed-ready') {
      console.debug(
        `[code-server bridge] embed-ready agents=${verdict.agents.length}`
        + ` bootstrap=${verdict.bootstrapVersion}`
      );
      // `ev.source`/origin never leave this component (§8.3) — passed
      // directly to the wizard, never through an ngrx action. The
      // announce's own `agents` payload is validated above but no longer
      // forwarded: the roster is the static registry the component renders
      // directly, not something to reconcile. Re-enter the zone: this whole
      // handler runs outside it, but the wizard's signal writes must be seen
      // by change detection (the onboard-wizard component's OnPush template
      // reads them).
      if (event.source) {
        const source = event.source;
        this.#ngZone.run(() => this.#onboardWizard.onEmbedReady(source, event.origin));
      }
      // Container-owned default (§1.3/§8.3): fires only when the wizard
      // ISN'T the one answering this announce — while active it already
      // sent its own directive via `onEmbedReady` above, and a second,
      // contradictory one must never go out for the same announce.
      if (!this.#onboardWizard.active()) {
        this.#sendSetMode(event.source, event.origin, 'standard');
      }
      return EMPTY;
    }

    if (verdict.verdict === 'agent-ready') {
      console.debug(`[code-server bridge] agent-ready agent=${verdict.agentId} event=${verdict.eventId.slice(0, 8)}`);
      this.#ngZone.run(() => this.#onboardWizard.onAgentReady(verdict.eventId));
      return EMPTY;
    }

    if (verdict.verdict === 'launch-failed') {
      console.debug(
        `[code-server bridge] launch-failed agent=${verdict.agentId}`
        + ` reason=${verdict.reason} event=${verdict.eventId.slice(0, 8)}`
      );
      this.#ngZone.run(() => this.#onboardWizard.onLaunchFailed(verdict.eventId));
      return EMPTY;
    }

    // Truthful ack: do NOT ack accepted:true yet. Stash the sender's
    // Window + origin (never into an NgRx action) keyed by eventId, then
    // dispatch manualOpen with requestId == eventId. The manualOpenResult
    // listener below acks once the dialog-open effect confirms the
    // container actually resolved (accept) or gave up (not-ready).
    this.#pendingBridgeAcks.set(verdict.eventId, { source: event.source, origin: event.origin });
    console.debug(`[code-server bridge] manual-open request event=${verdict.eventId.slice(0, 8)} agent=${verdict.agentType}`);

    // Re-enter the zone only for the store dispatch — everything above in
    // this handler stays outside it.
    this.#ngZone.run(() => {
      this.#store.dispatch(zcpAgentAuthDialogActions.manualOpen({
        serviceStackId: verdict.serviceStackId,
        agentType: verdict.agentType,
        clientId: verdict.clientId,
        requestId: verdict.eventId
      }));
    });

    return EMPTY;
  }

  // Walks the message source up to the embedded iframe's contentWindow (max 8
  // parent edges). Needs live `Window` objects, so it stays in the component;
  // its boolean result feeds the pure verdict as `fromCurrentIframe`.
  #isFromCurrentIframe(source: MessageEventSource | null): boolean {
    const iframeWindow = this.iframeRef()?.nativeElement.contentWindow;
    if (!source || !iframeWindow) return false;

    let current = source as Window;
    for (let hops = 0; hops <= 8; hops++) {
      if (current === iframeWindow) return true;
      try {
        const parent = current.parent;
        if (!parent || parent === current) return false;
        current = parent;
      } catch {
        return false;
      }
    }

    return false;
  }

  // Record a consumed eventId (case-normalized) and keep the seen-set bounded
  // by evicting the oldest once it exceeds the cap.
  #recordSeenEvent(eventId: string) {
    this.#seenBridgeEventIds.add(eventId.toLowerCase());
    if (this.#seenBridgeEventIds.size > MAX_SEEN_BRIDGE_EVENTS) {
      const oldestEventId = this.#seenBridgeEventIds.values().next().value;
      if (oldestEventId) this.#seenBridgeEventIds.delete(oldestEventId);
    }
  }

  #ackBridgeMessage(
    source: MessageEventSource | null,
    origin: string,
    eventId: string,
    outcome: { accepted: boolean; reason?: AgentAuthBridgeAckReason }
  ) {
    const ack: AgentAuthBridgeAckMessage = {
      channel: AGENT_AUTH_BRIDGE_CHANNEL,
      version: 1,
      type: AGENT_AUTH_BRIDGE_ACK_TYPE,
      eventId,
      ...outcome
    };

    try {
      (source as Window)?.postMessage(ack, origin);
    } catch {
      // Sender navigated away or closed before the ack could be delivered.
    }
  }

  // Sends the FE→embed `set-mode` directive (§4.3). Its own eventId/createdAt
  // are minted fresh here at send time — set-mode has no cross-message
  // identity to preserve, unlike an ack (which echoes the inbound eventId) or
  // launch-agent's retried intent.
  #sendSetMode(source: MessageEventSource | null, origin: string, mode: EmbedWizardMode) {
    const message = buildSetModeMessage(mode, crypto.randomUUID(), Date.now());

    try {
      (source as Window)?.postMessage(message, origin);
    } catch {
      // Sender navigated away or closed before the directive could be delivered.
    }
  }
}
